import { useRef, useState } from "react";

import type { AlbumSlug } from "../content/gallery";
import { ALBUMS } from "../content/gallery";
import { t } from "../i18n/dict";
import { langFromPath, localePath } from "../i18n/lang";
import { Page } from "../layout/Page";
import { Lightbox } from "../ui/Lightbox";
import { Masonry } from "../ui/Masonry";
import { Section } from "../ui/Section";

/**
 * One gallery album: the old site's `/lifestyle-fair-2022-aug`,
 * `/meet-greet-meditate-share-2022-jun` or `/old-photos`. A responsive
 * masonry of the album's photographs, each opening the shared `Lightbox`
 * at its own index, followed by the album's video highlights where it has
 * them. Each video is `preload="none"` behind its poster frame, so the
 * grid costs a few kilobytes until someone presses play.
 */
export function AlbumPage({ slug }: { slug: AlbumSlug }) {
  const { lang } = langFromPath(window.location.pathname);
  const album = ALBUMS[slug];
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);

  function openLightbox(index: number, trigger: HTMLButtonElement) {
    triggerRef.current = trigger;
    setLightboxIndex(index);
    setLightboxOpen(true);
  }

  return (
    <Page title={album.title} heading={album.title}>
      <Section>
        <a
          href={localePath("/gallery", lang)}
          className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-teal-deep underline-offset-4 hover:underline"
        >
          <span aria-hidden="true">←</span>
          {t(lang, "backToGallery")}
        </a>
        <div className="mt-6">
          <Masonry photos={album.photos} onOpen={openLightbox} />
        </div>
      </Section>
      {album.videos.length > 0 ? (
        <Section title="Video highlights" tone="ivory-deep">
          <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {album.videos.map((v) => (
              <li key={v.src}>
                <video
                  className="aspect-[4/3] w-full rounded-2xl border border-hairline bg-ink/5 object-contain shadow-soft"
                  src={v.src}
                  poster={v.poster}
                  controls
                  preload="none"
                  playsInline
                  aria-label={v.label}
                />
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      <Lightbox
        open={lightboxOpen}
        photos={album.photos}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxOpen(false)}
        albumTitle={album.title}
        returnFocusTo={triggerRef}
      />
    </Page>
  );
}
